import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Camera, Upload, Trash2, Loader2, X } from "lucide-react";

/**
 * Commissioning-photo gallery for a Service Ticket.
 *   canUpload : boolean — controls upload UI (technician / SI / admin)
 *   canDelete : boolean — controls delete buttons (uploader logic still enforced server-side)
 *   compact   : boolean — thumbnails-only layout for embedded cards
 */
export default function TicketPhotoGallery({ ticketId, canUpload = true, canDelete = true, compact = false }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null); // photo object
  const fileRef = useRef(null);

  const backend = process.env.REACT_APP_BACKEND_URL;

  const reload = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/service-tickets/${ticketId}/photos`);
      setPhotos(data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => { reload(); }, [reload]);

  async function onPick(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const form = new FormData();
        form.append("file", f);
        await api.post(`/service-tickets/${ticketId}/photos`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`Uploaded ${files.length} photo${files.length > 1 ? "s" : ""}`);
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function onDelete(p) {
    if (!window.confirm(`Delete "${p.filename}"?`)) return;
    try {
      await api.delete(`/service-tickets/${ticketId}/photos/${p.id}`);
      setPhotos((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Photo deleted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }

  return (
    <div data-testid="ticket-photo-gallery">
      {!compact && (
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Camera className="w-3.5 h-3.5 text-[#1F4E2C]" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#1F4E2C]">
              Commissioning Photos · {photos.length}
            </span>
          </div>
          {canUpload && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                onChange={onPick}
                className="hidden"
                data-testid="ticket-photo-input"
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="ticket-photo-upload-btn"
                className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest bg-[#1F4E2C] text-white px-2.5 py-1.5 hover:bg-[#0F1A12] disabled:opacity-60"
              >
                {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                {uploading ? "Uploading…" : "Add Photos"}
              </button>
            </>
          )}
        </div>
      )}

      {loading && photos.length === 0 ? (
        <div className="text-[11px] text-slate-500 font-mono py-2">Loading…</div>
      ) : photos.length === 0 ? (
        <div className={"text-[11px] text-slate-500 font-mono text-center " + (compact ? "py-1" : "border border-dashed border-slate-300 px-3 py-4")}>
          {compact ? "No photos yet." : <>No commissioning photos yet. {canUpload && "Add JPG / PNG / WEBP up to 10 MB."}</>}
        </div>
      ) : (
        <div className={"grid gap-2 " + (compact ? "grid-cols-4" : "grid-cols-3 sm:grid-cols-4 md:grid-cols-6")}>
          {photos.map((p) => (
            <div key={p.id} className="relative group aspect-square bg-slate-100 overflow-hidden border border-slate-200 rounded-sm">
              <button
                type="button"
                onClick={() => setLightbox(p)}
                data-testid={`ticket-photo-thumb-${p.id}`}
                className="block w-full h-full"
              >
                <img
                  src={`${backend}/api/files/${p.id}`}
                  alt={p.filename}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                />
              </button>
              {canDelete && !compact && (
                <button
                  type="button"
                  onClick={() => onDelete(p)}
                  data-testid={`ticket-photo-delete-${p.id}`}
                  className="absolute top-1 right-1 p-1 bg-white/90 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity rounded-sm hover:bg-white"
                  title="Delete photo"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {lightbox && (
        <div
          data-testid="ticket-photo-lightbox"
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white"
            data-testid="ticket-photo-lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={`${backend}/api/files/${lightbox.id}`}
            alt={lightbox.filename}
            onClick={(e) => e.stopPropagation()}
            className="max-h-full max-w-full object-contain"
          />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 font-mono text-xs">
            {lightbox.filename} · uploaded by {lightbox.uploaded_by_name || "—"}
          </div>
        </div>
      )}
    </div>
  );
}
